import {withHandlers} from 'recompose'

import chevronDown from '@jetbrains/icons/chevron-down.svg'
import chevronRight from '@jetbrains/icons/chevron-right.svg'
import {H2} from '@jetbrains/ring-ui/components/heading/heading'
import Icon from '@jetbrains/ring-ui/components/icon/icon'
import classNames from 'classnames'
import compose from 'lodash/flowRight'
import * as React from 'react'
import {connect} from 'react-redux'

import {collapseBuildTypeBuildsSection, expandBuildTypeBuildsSection} from '../../../../actions'
import EntityLink from '../../../../containers/EntityLink'
import {withOneLineProp} from '../../../../contexts/oneLine'
import {useDerivedState} from '../../../../hocs/withDerivedState'
import {withBranchProps} from '../../../../hooks/routes'
import type {State} from '../../../../reducers/types'
import {
  getBuildType,
  getBuildTypeDescription,
  isProjectBuildTypeLineCollapsed,
} from '../../../../selectors'
import type {Branch} from '../../../../services/rest'
import type {BuildTypeId} from '../../../../types'
import PendingChangesDropdown from '../../../packages/Changes/PendingChangesDropdown/PendingChangesDropdown'
import OverviewSystemProblems from '../../../pages/BuildTypePage/BuildTypePageHeader/OverviewSystemProblems/OverviewSystemProblems.container'
import OverviewBuildTypeActions from '../../BuildTypeActionsDropdown/BuildTypeActionsDropdown'
import Details from '../../Details/Details'
import FavoriteStar from '../../FavoriteStar/FavoriteStar.container'
import RowGroup from '../../Grid/RowGroup'
import MiddleEllipsis from '../../MiddleEllipsis/MiddleEllipsis'
import OverviewStatusIcon from '../../ProjectOrBuildTypeIcon/OverviewStatusIcon.container'
import {EntityIconType} from '../../ProjectOrBuildTypeIcon/ProjectOrBuildTypeIcon.types'
import RunBuildButtonGroup from '../../RunBuild/RunBuildInSelectedBranch.container'
import {
  SequenceLoaderContext,
  sequenceLoaderDefaultContext,
  useSequenceLoaderReadyHandler,
} from '../../SequenceLoader/SequenceLoader.hooks'
import {useVisibility} from '../../Visible/Visible.hooks'

import type {
  ContainerActionProps,
  ContainerOwnProps,
  ContainerProps,
  ContainerStateProps,
} from './BuildTypeLine.types'
import BuildTypePauseDropdown from './BuildTypePauseDropdown/BuildTypePauseDropdown'

import styles from './BuildTypeLine.css'

import {useAppSelector} from 'src/hooks/react-redux'

type ChangesColumnContentProps = {
  buildTypeId: BuildTypeId
  branch: Branch
}

const mapStateToProps = (
  state: State,
  {isFavorite, buildTypeId}: ContainerOwnProps,
): ContainerStateProps => {
  const expanded = !isProjectBuildTypeLineCollapsed(state, buildTypeId, !isFavorite)
  return {
    expanded,
  }
}

const actionCreators: ContainerActionProps = {
  collapse: collapseBuildTypeBuildsSection,
  expand: expandBuildTypeBuildsSection,
}

const ChangesColumnContent = React.memo(({buildTypeId, branch}: ChangesColumnContentProps) => {
  const ref = React.useRef<HTMLDivElement>(null)
  const isVisible = useVisibility(ref)
  const wasVisible = useDerivedState(false, state => state || isVisible)
  return (
    <div ref={ref}>
      {wasVisible ? <PendingChangesDropdown buildTypeId={buildTypeId} branch={branch} /> : null}
    </div>
  )
})
ChangesColumnContent.displayName = 'ChangesColumnContent'

function BuildTypeLine(props: ContainerProps) {
  const {
    buildTypeId,
    branch,
    oneLine,
    expanded,
    archivedProject,
    toggleExpand,
    hasContent,
    emptyText,
    children,
  } = props

  const name = useAppSelector(state => getBuildType(state, buildTypeId)?.name)
  const description = useAppSelector(state => getBuildTypeDescription(state, buildTypeId) ?? null)
  const paused = useAppSelector(state => getBuildType(state, buildTypeId)?.paused ?? false)

  const summaryClass = classNames(styles.root, {
    [styles.oneLine]: oneLine,
    [styles.expanded]: expanded,
  })

  const buildTypeInfoClasses = classNames(styles.buildTypeInfo)

  const actionsContent = React.useMemo(
    () => (
      <div className={styles.runColumn}>
        <RunBuildButtonGroup buildTypeId={buildTypeId} />
        <OverviewBuildTypeActions
          compact
          withEdit
          buildTypeId={buildTypeId}
          className={styles.actions}
        />
      </div>
    ),
    [buildTypeId],
  )
  const Summary = React.useMemo(
    () => (
      <SequenceLoaderContext.Provider value={sequenceLoaderDefaultContext}>
        <RowGroup>
          <div className={buildTypeInfoClasses}>
            <H2 className={styles.buildTypeName}>
              <OverviewStatusIcon
                type={EntityIconType.BUILD_TYPE}
                id={buildTypeId}
                size="S"
                showInvestigationInStatus
              />
              <EntityLink title={name} buildTypeId={buildTypeId} className={styles.link}>
                <MiddleEllipsis tailLength={3} className={styles.caption}>
                  {name ?? ''}
                </MiddleEllipsis>
              </EntityLink>
            </H2>
            {!archivedProject && <FavoriteStar buildTypeId={buildTypeId} className={styles.star} />}
            {hasContent === false && <span className={styles.empty}>{emptyText}</span>}
            {description != null && (
              <span className={styles.description}>
                <MiddleEllipsis tailLength={5}>{description}</MiddleEllipsis>
              </span>
            )}
            {paused && <BuildTypePauseDropdown buildTypeId={buildTypeId} />}
            <OverviewSystemProblems
              className={styles.systemProblems}
              buildTypeId={buildTypeId}
              branch={branch}
            />
          </div>
          <div className={styles.changes}>
            <ChangesColumnContent buildTypeId={buildTypeId} branch={branch} />
          </div>
          {actionsContent}
          <Icon
            glyph={expanded ? chevronDown : chevronRight}
            className={classNames(styles.chevron)}
          />
        </RowGroup>
      </SequenceLoaderContext.Provider>
    ),

    [
      buildTypeInfoClasses,
      buildTypeId,
      name,
      archivedProject,
      hasContent,
      emptyText,
      description,
      paused,
      branch,
      actionsContent,
      expanded,
    ],
  )
  useSequenceLoaderReadyHandler(!expanded)

  return (
    <Details
      label={name ?? ''}
      onToggle={toggleExpand}
      open={expanded}
      renderButtonAside
      level={2}
      imitateHeading
      summaryClassName={styles.button}
      headingClassName={summaryClass}
      detailsWrapperClassName={styles.details}
      detailsClassName={styles.details}
      summary={Summary}
    >
      {children}
    </Details>
  )
}

export default compose(
  connect(mapStateToProps, actionCreators),
  withHandlers({
    toggleExpand:
      ({
        buildTypeId,
        collapse,
        expand,
        expanded,
      }: ContainerOwnProps & ContainerStateProps & ContainerActionProps) =>
      () => {
        if (expanded) {
          collapse(buildTypeId)
        } else {
          expand(buildTypeId)
        }
      },
  }),
  React.memo,
  withOneLineProp,
  withBranchProps,
  React.memo,
)(BuildTypeLine)
